import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { FunctionComponent, useEffect } from 'react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { Global } from '@emotion/react';
import Chrome from '../components/chrome';
import { Theme } from '../components/theme';
import { globalStyes } from '../styles/global.styles';
import ToastProvider from '@lib/context/toast.context';
import { SessionProvider } from 'next-auth/react';
import { Session } from 'next-auth';
import AuthHandler from '@components/auth/auth-handler';
import { ErrorBoundary } from '@appsignal/react';
import { appsignal, initAppsignal } from '@lib/appsignal';
import RouteGuard from '@components/route-guard';
import { EuiButton, EuiButtonEmpty, EuiEmptyPrompt } from '@elastic/eui';
import { ApolloProviderWrapper } from '@data/apollo-client';

/**
 * Next.js uses the App component to initialize pages. You can override it
 * and control the page initialization. Here use use it to render the
 * `Chrome` component on each page, and apply an error boundary.
 *
 * @see https://nextjs.org/docs/advanced-features/custom-app
 */

const EuiApp: FunctionComponent<AppProps<{ session: Session }>> = ({
  Component,
  pageProps: { session, ...pageProps },
}) => {
  // Initialize appsignal plugins. TODO: Could this be handled better?
  useEffect(() => {
    initAppsignal();
  }, []);

  return (
    <>
      <Head>
        {/* You can override this in other pages - see index.tsx for an example */}
        <title>Auth Admin</title>
      </Head>
      <Global styles={globalStyes} />
      <SessionProvider
        session={session}
        refetchInterval={60} // refresh session every 60 seconds
        refetchOnWindowFocus={true}
        refetchWhenOffline={false}>
        <ToastProvider>
          <Theme>
            <Chrome>
              <ErrorBoundary
                instance={appsignal}
                fallback={error => (
                  <EuiEmptyPrompt
                    color="danger"
                    iconType="error"
                    title={<h2>Something went wrong.</h2>}
                    body={
                      <>
                        <p>{error?.message}</p>
                      </>
                    }
                    actions={[
                      <EuiButton
                        key={1}
                        fill
                        onClick={() => window.location.replace('/')}>
                        Return to home page
                      </EuiButton>,
                      <EuiButtonEmpty
                        key={2}
                        onClick={() => window.location.reload()}>
                        Try again
                      </EuiButtonEmpty>,
                    ]}
                  />
                )}>
                <AuthHandler />
                <RouteGuard>
                  <ApolloProviderWrapper>
                    <Component {...pageProps} />
                  </ApolloProviderWrapper>
                </RouteGuard>
              </ErrorBoundary>
            </Chrome>
          </Theme>
        </ToastProvider>
      </SessionProvider>
    </>
  );
};

export default EuiApp;
